<script lang="ts">
import { computed, type ComputedRef, defineAsyncComponent, defineComponent, inject, onMounted, ref, type Ref, toRaw } from 'vue';
import { PageStore } from 'stores/Page/Page.ts';
import { BasketStore } from 'stores/Basket/Basket.ts';
import { storeToRefs } from 'pinia';
import type { AttributeValueWebItem, AttributeWebItem, ProductVariation, ProductWebItem } from 'src/core/types/Products';
import { getLowestPriceVariation, getVariationImages } from 'src/core/utils/Products';
import { useMeta } from 'quasar';

const ssrMode = import.meta.env.SSR;

import { useQuasar } from 'quasar';
import InputPiece from 'components/Product/Detail/InputPiece.vue';
import { ResponseContext } from 'src/core/api/ProviderContext';
import { FavouriteStore } from 'stores/Favourite/Favourite.ts';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'Product',

  components: {
    InputPiece,
  },

  data: () => ({
    tabsData: {
      tab: 'aciklama',
    },
  }),

  setup() {
    const $q = useQuasar();
    const $router = useRouter();
    const pageStore = PageStore();
    const { head, content } = storeToRefs(pageStore);

    const isLogged = inject<Ref<boolean>>('isLogged');

    let product: ProductWebItem = content.value;
    const lowestPriceVariation: ProductVariation = getLowestPriceVariation(product.variations);
    const currentVariationIndex = ref(product.variations.indexOf(lowestPriceVariation));
    const variationsImageList = getVariationImages(product.variations);
    const onSale = computed(() => !!product.publish);
    const outOfStock = product.variations.every((variation) => Number(variation.stock) === 0);

    /**
     * Varyasyon listeleme sıralaması düzenle
     */
    const baseOrder = product.attributeValues.map((o) => o.code);
    product = sortProductVariations(product);

    const activeVariation: ComputedRef<ProductVariation> = computed(() => product.variations[currentVariationIndex.value]);
    const isDiscount = computed(() => !!product.variations[currentVariationIndex.value].discount && !outOfStock); // Stokta yoksa indirimin de anlamı yok

    interface AttributeWebItemOnProduct extends AttributeWebItem {
      values: AttributeValueWebItemOnProduct[] | never[];
    }

    interface AttributeValueWebItemOnProduct extends AttributeValueWebItem {
      selected: Ref<boolean>;
      onExists: Ref<boolean>;
    }

    const attributeList: AttributeWebItemOnProduct[] = [];

    product.attributes.forEach((attr) => {
      // set attribute values
      const attributeValues = product.attributeValues.map((attrValue) => ({
        ...attrValue,
        selected: ref(false),
        onExists: ref(true),
      }));

      // set attribute values to attr main item
      const attrValuesFromList = attributeValues.filter((o) => o.attr === attr.code) || [];
      const attributeItem = { ...attr, values: attrValuesFromList };

      attributeList.push(attributeItem);
    });
    const attrArray = Object.keys(product.attributes).map((i) => Number(i));

    /**
     * Seçilen attr values, her bir attr için attrCode = attrValueCode formatında tutulur
     */
    const variationSelections: Ref<{ [attrKey: number]: number | null }> = ref({});
    attrArray.map((o) => (variationSelections.value[o] = null));

    function selectAttributeItem(attrIndex: number, attrValueCode: number) {
      if (variationSelections.value[attrIndex] === attrValueCode) return;

      // set new selection
      variationSelections.value[attrIndex] = attrValueCode;

      // reset next items
      Object.keys(variationSelections.value)
        .slice(attrIndex + 1)
        .forEach((index) => {
          variationSelections.value[Number(index)] = null;
        });

      let detectedVariationAttrs: string[] = [];
      // set the next index
      const next = attrIndex + 1;
      product.variations.forEach((variation) => {
        const indexOf = variation.attrs.indexOf(String(attrValueCode));
        if (indexOf > -1 && attrIndex === indexOf) {
          detectedVariationAttrs.push(variation.attrs[next]);
        }
      });

      // Mevcut seçimlere göre en uygun varyasyonu bul
      const currentSelections = Object.entries(variationSelections.value)
        .filter(([_, code]) => code !== null)
        .map(([index, code]) => ({ index: Number(index), code: String(code) }));

      const currentVariation = product.variations.find((variation) => {
        return currentSelections.every(({ index, code }) => {
          return variation.attrs[index] === code;
        });
      });

      if (currentVariation) {
        currentVariationIndex.value = product.variations.indexOf(currentVariation);
      } else {
        currentVariationIndex.value = 0; // Eşleşen varyasyon yoksa
      }

      // if (attrIndex === 0) {
      //   const currentVariation = product.variations.find((o: ProductVariation) => Number(o.attrs[0]) === attrValueCode);
      //
      //   if (currentVariation) {
      //     currentVariationIndex.value = product.variations.indexOf(currentVariation);
      //   }
      // }

      if (!attributeList[next]) return;

      attributeList[next].values.forEach((attrValue) => (attrValue.onExists.value = true));
      attributeList[next].values.forEach((attrValue) => {
        attrValue.onExists.value = detectedVariationAttrs.includes(String(attrValue.code));
      });
    }

    /**
     * İndirim kontrolü
     */

    /**
     * Tüm Özellikleri Seçilmiş Olan Varyasyonun Stok Durumunu Kontrol et
     */
    const selectedVariationStockOut: ComputedRef<ProductVariation | null> = computed(() => {
      const selections = Object.values(variationSelections.value);
      return (
        product.variations.find((variation) => {
          return selections.every((o) => variation.attrs.includes(String(o)));
        }) || null
      );
    });

    /**
     * Ürünleri stok durumlarının kontrolü
     */
    const stockState: { index: number; code: number; state: boolean; attrs: string[] }[] = [];
    product.variations.forEach((variation, index) => {
      stockState.push({
        index,
        code: variation.code,
        state: !!variation.stock,
        attrs: variation.attrs,
      });
    });

    const inStock: boolean = stockState.every((o) => o.state);

    if (product.type && inStock && onSale.value) {
      const firstVariationStateCode = stockState.find((o) => o.code === lowestPriceVariation.code)?.attrs[0];
      selectAttributeItem(0, Number(firstVariationStateCode));
    }

    const quantity = ref(1);

    const addToBasketLoading = ref(false);

    const onSlideChange = () => {
      console.log('slide change');
    };

    const thumbsSwiper = ref(null);

    const setThumbsSwiper = (swiper) => {
      thumbsSwiper.value = swiper;
    };

    /**
     * Favourite state management
     */
    const favouriteStore = FavouriteStore();
    const favouriteLoading = ref(false);

    const FreeMode = ref(null);
    const Navigation = ref(null);
    const Thumbs = ref(null);
    let Swiper = null,
      SwiperSlide = null;

    if (!ssrMode) {
      onMounted(async () => {
        const SwiperModules = await import('swiper/modules');
        FreeMode.value = SwiperModules.FreeMode;
        Navigation.value = SwiperModules.Navigation;
        Thumbs.value = SwiperModules.Thumbs;
      });
    }

    const favouriteState = ref({
      loading: false,
      status: false,
    });

    if (isLogged?.value) {
      checkFavouriteState();
    }

    function checkFavouriteState() {
      favouriteState.value.loading = true;
      // Ürün favorilere eklenmiş mi kontrolü
      favouriteStore
        .productInFavourite(product.code)
        .then(({ status, data }) => {
          if (status._200_ && data) {
            favouriteState.value.status = true;
          } else {
            favouriteState.value.status = false;
          }
          favouriteState.value.loading = false;
        })
        .catch(() => {
          favouriteState.value.loading = false;
        });
    }

    // Schema.org JSON-LD yapısını oluştur
    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      sku: product.kod,
      image: product.variations[0].images || [],
      description: product.desc || '',
      category: product.category.name,
      // material: product.ozellikler.find((o) => o.name === 'Malzeme')?.value || '',
      // color: product.attributeValues.filter((attr) => attr.attr === 'Renk').map((attr) => attr.name),
      offers: product.variations.map((variation) => ({
        '@type': 'Offer',
        url: `https://hazeljewellery.com/${product.link}`,
        priceCurrency: 'TRY',
        price: variation.priceDecimal,
        itemCondition: 'https://schema.org/NewCondition',
        availability: variation.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        sku: variation.kod,
        priceValidUntil: new Date(new Date(variation.date).setFullYear(new Date(variation.date).getFullYear() + 1)).toISOString(),
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'TR',
          },
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '65.00',
            currency: 'TRY',
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 1,
              maxValue: 5,
              unitCode: 'd',
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 1,
              maxValue: 9,
              unitCode: 'd',
            },
          },
          transitTimeLabel: 'Standart Kargo',
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 15,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
          returnPolicyCountry: 'TR',
          applicableCountry: 'TR',
        },
      })),
      additionalProperty: product.ozellikler.map((feature) => ({
        '@type': 'PropertyValue',
        name: feature.name,
        value: feature.value,
      })),
    };

    const siteAddress = head.value.title.siteAddress.endsWith('/') ? head.value.title.siteAddress : `${head.value.title.siteAddress}/`;

    const openGraph = {
      'og:title': `${product.name} | ${product.category.name}`,
      'og:type': 'product',
      'og:url': `${siteAddress}${product.link}`,
      'og:image': product.variations[0].images?.[0] || '',
      'og:image:alt': product.name,
      'og:description': product.desc || '',
      'og:site_name': head.value.title.siteTitle,
      'og:locale': head.value.info.lang.replace('-', '_'),
      'og:updated_time': new Date().toISOString(),
      'product:price:amount': lowestPriceVariation.priceDecimal,
      'product:price:currency': 'TRY',
      'product:availability': !outOfStock ? 'in stock' : 'out of stock',
    };

    const twitterGraph = {
      'twitter:card': 'summary_large_image',
      'twitter:title': `${product.name} | ${product.category.name}`,
      'twitter:description': product.desc || '',
      'twitter:image': product.variations[0].images?.[0] || '',
      'twitter:image:alt': product.name,
      'twitter:site': head.value.title.siteAddress,
    };

    const metas = {};
    if (product.desc) {
      metas.description = {
        name: 'description',
        content: product.desc,
      };
    }

    for (const name of Object.keys(openGraph)) {
      if (openGraph[name]) {
        metas[name] = { property: name, content: openGraph[name] };
      }
    }
    for (const name of Object.keys(twitterGraph)) {
      if (twitterGraph[name]) {
        metas[name] = { name: name, content: twitterGraph[name] };
      }
    }

    // Meta bilgiyi Quasar metaInfo ile ekle
    useMeta({
      titleTemplate: (title) => `${title} | ${product.category.name} | ${product.name}`,
      meta: metas,
      link: {
        canonical: { rel: 'canonical', href: `${siteAddress}${product.link}` },
      },
      script: {
        schemaProduct: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(schema),
        },
      },
    });

    function sortAttrsByBaseOrder(attrs) {
      return attrs
        .map((code) => parseInt(code)) // string -> number
        .sort((a, b) => baseOrder.indexOf(a) - baseOrder.indexOf(b))
        .map((code) => code.toString()); // tekrar string'e çevir
    }

    function sortProductVariations(product) {
      if (Array.isArray(product.variations)) {
        product.variations = product.variations.map((variation) => {
          const sortedAttrs = sortAttrsByBaseOrder(variation.attrs || []);
          return {
            ...variation,
            attrs: sortedAttrs,
          };
        });
      }
      return product;
    }

    return {
      isLogged,
      isSingleProduct: product.type === 0,
      product,
      inStock,
      outOfStock,
      currentVariationIndex,
      activeVariation,
      onSale,
      isDiscount,
      selectedVariationStockOut,
      variationsImageList,
      attributeList,
      variationSelections,
      selectAttributeItem,
      quantity,
      addToBasketLoading,
      onSlideChange,
      thumbsSwiper,
      setThumbsSwiper,
      Swiper,
      SwiperSlide,
      FreeMode,
      Navigation,
      Thumbs,
      swiper: ref(null),
      productVariations: product.variations,
      favouriteLoading,
      favouriteState,
      addToFavourites: (productCode: number) => {
        if (!isLogged?.value) {
          $router.push('/login-or-register');
          return;
        }
        favouriteLoading.value = true;
        favouriteStore
          .addToFavourites(productCode)
          .then(({ status, message }) => {
            if (status._200_) {
              $q.notify({
                color: 'green',
                icon: 'fa-regular fa-circle-check',
                message: 'Ürün favori listenize eklenmiştir',
                badgeColor: 'white',
                badgeTextColor: 'green-10',
              });
              checkFavouriteState();
            } else {
              $q.notify({
                color: 'red',
                icon: 'fa-solid fa-circle-exclamation',
                message,
              });
            }
            favouriteLoading.value = false;
          })
          .catch(() => {
            favouriteLoading.value = false;
          });
      },
      removeFromFavourites: (productCode: number) => {
        favouriteLoading.value = true;
        favouriteStore
          .favouriteRemove(productCode)
          .then(({ status, message }) => {
            if (status._200_) {
              $q.notify({
                color: 'green',
                icon: 'fa-regular fa-circle-check',
                message: 'Ürün favori listenizden çıkartılmıştır',
                badgeColor: 'white',
                badgeTextColor: 'green-10',
              });
              checkFavouriteState();
            } else {
              $q.notify({
                color: 'red',
                icon: 'fa-solid fa-circle-exclamation',
                message,
              });
            }
            favouriteLoading.value = false;
          })
          .catch(() => {
            favouriteLoading.value = false;
          });
      },
    };
  },

  methods: {
    toRaw,
    // moveOnSwiperImg(event) {
    //   var x = event.clientX - lastX;
    //   var y = event.clientY - lastY;
    //
    //   img.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
    //
    //   lastX = event.clientX;
    //   lastY = event.clientY;
    // },
    onSwiper(swiper) {
      this.swiper = swiper;
    },
    slidePrev() {
      this.swiper.slidePrev();
    },
    slideNext() {
      this.swiper.slideNext();
    },

    addToBasket() {
      const hasSelectedAll = Object.keys(this.variationSelections).every((o: string) => this.variationSelections[Number(o)]);
      if (!hasSelectedAll) {
        this.$q.notify({
          message: 'Lütfen almak istediğiniz ürünün tüm özelliklerini seçiniz!',
          // caption: '5 minutes ago',
          color: 'secondary',
          position: 'center',
          icon: 'fa-solid fa-triangle-exclamation',
          timeout: 500,
        });
      } else {
        this.addToBasketLoading = true;
        const selections = Object.values(this.variationSelections);
        const basketStore = BasketStore();
        /**
         * Find selected variation in based selected attr values
         */
        const variations = this.product.variations;
        let selectedVariation: ProductVariation | null = null;
        variations.forEach((variation: ProductVariation) => {
          if (selections.every((o) => variation.attrs.includes(String(o)))) {
            selectedVariation = variation;
          }
        });
        basketStore
          .addToBasket(selectedVariation?.code, this.quantity)
          .then(({ status, message, data }: ResponseContext) => {
            console.log(message);
            if (status._200_) {
              this.$q.notify({
                color: 'green',
                icon: 'fa-regular fa-circle-check',
                message: 'Ürün başarılı bir şekilde sepete eklendi',
              });
              const { userBasketCount } = storeToRefs(basketStore);

              userBasketCount.value = data.length;
            } else {
              this.$q.notify({
                color: 'red',
                icon: 'fa-solid fa-circle-exclamation',
                message,
              });
            }
            this.addToBasketLoading = false;
          })
          .catch(() => {
            this.addToBasketLoading = false;
          });
      }
    },

    moveOnSwiperImg(event) {
      // console.log(event);
    },
  },

  mounted() {
    import('swiper/vue')
      .then(({ Swiper, SwiperSlide }) => {
        this.Swiper = Swiper;
        this.SwiperSlide = SwiperSlide;
      })
      .then(() => {
        this.$forceUpdate();
      });
  },
});
</script>

<template>
  <div id="productDetail" class="container">
    <div class="row">
      <div class="col-xs-12 col-md-4 q-pr-lg-md q-py-md">
        <div v-if="FreeMode" class="product-detail--slider">
          <!--        <swiper class="swiper swiper-urun-swiper" ref="swiperTop" :slides-per-view="3" :space-between="50" :options="swiperOptionTop">-->
          <!--          <swiper-slide :key="key" v-for="(resim, key) in product.resimler[aktifRenk]">-->
          <!--            <img :src="`${$ApiUrl}${$UrunResimResized}/460/318/${resim}`" />-->
          <!--          </swiper-slide>-->
          <!--          <div class="swiper-button-next text-transparent" @click="slideToNext" slot="button-next">-->
          <!--            <q-btn icon="chevron_right" round color="primary" />-->
          <!--          </div>-->
          <!--          <div class="swiper-button-prev text-transparent" @click="slideToPrev" slot="button-prev">-->
          <!--            <q-btn icon="chevron_left" round color="primary" />-->
          <!--          </div>-->
          <!--        </swiper>-->
          <!--        <swiper class="swiper gallery-thumbs" :options="swiperOptionThumbs" ref="swiperThumbs">-->
          <!--          <swiper-slide :key="key" v-for="(resim, key) in product.resimler[aktifRenk]">-->
          <!--            <img :src="`${$ApiUrl}${$UrunResimResized}/460/318/${resim}`" />-->
          <!--          </swiper-slide>-->
          <!--        </swiper>-->
          <component
            :is="Swiper"
            ref="swiperMainSlider"
            :style="{
              '--swiper-navigation-color': '#111',
              '--swiper-pagination-color': '#111',
            }"
            :loop="true"
            :spaceBetween="10"
            :navigation="true"
            :thumbs="{ swiper: thumbsSwiper }"
            :modules="[toRaw(FreeMode), toRaw(Navigation), toRaw(Thumbs)]"
            class="hzl-swiper-main"
            @swiper="onSwiper"
          >
            <template v-for="variation in productVariations">
              <component :is="SwiperSlide" :key="key" v-for="(img, key) in variationsImageList[variation.code]">
                <div ref="swiperWrapper" class="swiper-wrapper--img" @mousemove="moveOnSwiperImg($event)">
                  <img
                    :src="img.detail"
                    :srcset="`
                      ${img.small} 150w,
                      ${img.middle} 320w,
                      ${img.detail} 462w,
                      ${img.zoom} 1200w
                    `"
                    sizes="(max-width: 480px) 100vw, (max-width: 768px) 90vw, 462px"
                    :alt="product.name"
                  />
                </div>
              </component>
            </template>
          </component>

          <div class="slider--thumbs">
            <div class="hz-swiper-main-thumbs--nav thumbs--left">
              <q-btn round unelevated class="thumbs--s-btn" @click="slidePrev">
                <q-icon name="chevron_left" />
              </q-btn>
            </div>
            <component
              :is="Swiper"
              @swiper="setThumbsSwiper"
              :loop="true"
              :spaceBetween="10"
              :slidesPerView="5"
              :freeMode="true"
              :watchSlidesProgress="true"
              :modules="modules"
              class="hz-swiper-main-thumbs"
            >
              <template v-for="variation in productVariations">
                <component :is="SwiperSlide" :key="key" v-for="(img, key) in variationsImageList[variation.code]" class="swiper-slide-thumb">
                  <img
                    :src="img.small"
                    :srcset="`
                      ${img.small} 150w,
                      ${img.middle} 320w
                    `"
                    sizes="(max-width: 480px) 60px, (max-width: 768px) 80px, 120px"
                    :alt="product.name"
                    loading="lazy"
                    decoding="async"
                  />
                </component>
              </template>
            </component>
            <div class="hz-swiper-main-thumbs--nav thumbs--right">
              <q-btn round unelevated class="thumbs--s-btn" @click="slideNext">
                <q-icon name="chevron_right" />
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-md-7 q-px-lg q-py-md">
        <div id="product-detail--head">
          <h1 class="product-detail--head_title text-h5 text-weight-bold text-primary q-mb-xs">{{ product.name }}</h1>
          <p class="product-detail--head_product-code text-subtitle1 text-weight-thin">
            {{ product.kod }}
            <q-chip square v-if="isDiscount" class="discount q-ml-md" size=".6rem">
              <q-icon name="trending_down" size="1.2rem" />
              <span class="q-px-sm">İNDİRİMDEKİ ÜRÜN</span>
            </q-chip>
          </p>
        </div>
        <div id="urunOrtaBolum">
          <div class="urun-fiyat q-mb-lg">
            <p class="product-detail--pricing text-h6 text-weight-bold flex">
              <span class="current-price q-mr-sm" :class="{ 'on-sale-price': isDiscount }">{{ $numberToCurrency(activeVariation.priceNumber * quantity) }} ₺</span>
              <span v-if="isDiscount" class="on-sale-price on-discount-price">{{ $numberToCurrency(activeVariation.oldPriceNumber * quantity) }} ₺</span>
            </p>
          </div>
          <div v-if="!onSale" class="product-detail--not-on-sale">
            <p>Ürün Geçici Olarak Temin Edilememektedir</p>
          </div>
          <div v-else-if="outOfStock" class="product-detail--out-of-stock">
            <p>
              <svg viewBox="0 0 24 24" width="35px" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M17.6005 5.31125L11.9505 2.27125C11.3505 1.95125 10.6405 1.95125 10.0405 2.27125L4.40047 5.31125C3.99047 5.54125 3.73047 5.98125 3.73047 6.46125C3.73047 6.95125 3.98047 7.39125 4.40047 7.61125L10.0505 10.6512C10.3505 10.8112 10.6805 10.8913 11.0005 10.8913C11.3205 10.8913 11.6605 10.8112 11.9505 10.6512L17.6005 7.61125C18.0105 7.39125 18.2705 6.95125 18.2705 6.46125C18.2705 5.98125 18.0105 5.54125 17.6005 5.31125Z"
                    fill="#030201"
                  ></path>
                  <path
                    d="M9.12 11.7106L3.87 9.09058C3.46 8.88058 3 8.91058 2.61 9.14058C2.23 9.38058 2 9.79058 2 10.2406V15.2006C2 16.0606 2.48 16.8306 3.25 17.2206L8.5 19.8406C8.68 19.9306 8.88 19.9806 9.08 19.9806C9.31 19.9806 9.55 19.9106 9.76 19.7906C10.14 19.5506 10.37 19.1406 10.37 18.6906V13.7306C10.36 12.8706 9.88 12.1006 9.12 11.7106Z"
                    fill="#030201"
                  ></path>
                  <path
                    d="M20.0006 10.2406V12.7006C19.5206 12.5606 19.0106 12.5006 18.5006 12.5006C17.1406 12.5006 15.8106 12.9706 14.7606 13.8106C13.3206 14.9406 12.5006 16.6506 12.5006 18.5006C12.5006 18.9906 12.5606 19.4806 12.6906 19.9506C12.5406 19.9306 12.3906 19.8706 12.2506 19.7806C11.8706 19.5506 11.6406 19.1406 11.6406 18.6906V13.7306C11.6406 12.8706 12.1206 12.1006 12.8806 11.7106L18.1306 9.09058C18.5406 8.88058 19.0006 8.91058 19.3906 9.14058C19.7706 9.38058 20.0006 9.79058 20.0006 10.2406Z"
                    fill="#030201"
                  ></path>
                  <path
                    d="M21.6814 15.3206C20.7914 14.4306 19.6114 13.9806 18.4414 14.0006C17.3114 14.0106 16.1814 14.4606 15.3214 15.3206C14.7214 15.9106 14.3314 16.6506 14.1414 17.4206C14.0314 17.8406 13.9914 18.2706 14.0214 18.7006V18.7506C14.0214 18.8206 14.0314 18.8806 14.0414 18.9606C14.0414 18.9606 14.0414 18.9606 14.0514 18.9706V19.0006C14.1414 19.9806 14.5614 20.9306 15.3214 21.6806C16.4814 22.8406 18.1114 23.2306 19.5814 22.8606C20.0214 22.7506 20.4514 22.5706 20.8514 22.3306C21.1514 22.1606 21.4314 21.9406 21.6814 21.6806C22.4314 20.9306 22.8614 19.9806 22.9514 18.9906C22.9614 18.9906 22.9614 18.9706 22.9614 18.9606C22.9814 18.8906 22.9814 18.8106 22.9814 18.7406C22.9814 18.7306 22.9914 18.7106 22.9914 18.6906C23.0514 17.4806 22.6114 16.2406 21.6814 15.3206ZM20.2314 20.2106C19.9414 20.5006 19.4714 20.5006 19.1714 20.2106L18.5114 19.5506L17.8314 20.2306C17.5314 20.5306 17.0614 20.5306 16.7714 20.2306C16.4714 19.9406 16.4714 19.4706 16.7714 19.1706L17.4514 18.4906L16.7914 17.8306C16.5014 17.5306 16.5014 17.0606 16.7914 16.7706C17.0914 16.4706 17.5614 16.4706 17.8614 16.7706L18.5114 17.4306L19.1414 16.7906C19.4414 16.5006 19.9114 16.5006 20.2114 16.7906C20.5014 17.0906 20.5014 17.5606 20.2114 17.8606L19.5714 18.4906L20.2314 19.1406C20.5314 19.4406 20.5314 19.9106 20.2314 20.2106Z"
                    fill="#030201"
                  ></path>
                </g>
              </svg>
              Üzgünüz Ürün <strong class="text-weight-bold q-ml-sm">Stokta Kalmadı</strong>
            </p>
          </div>
          <div v-else class="product-detail--selection-features">
            <div :key="attr.code" v-for="(attr, index) in attributeList" class="selection-features--chips">
              <p class="product-detail--selection-title">{{ attr.name }}</p>
              <div
                class="product-detail--selection-chips"
                :class="{
                  disabled: variationSelections[index - 1] === null,
                  'pointer-none': variationSelections[index - 1] === null,
                }"
              >
                <template :key="key" v-for="(value, key) in attr.values">
                  <div
                    v-if="variationSelections[index] !== value.code"
                    :class="{
                      'inline-block': true,
                      disabled: !value.onExists.value,
                      'pointer-none': !value.onExists.value,
                    }"
                  >
                    <q-chip class="selection-chip_item" square color="white" clickable size=".95rem" icon-selected="" @click="selectAttributeItem(index, value.code)">
                      <q-icon name="fa-solid fa-plus" class="q-mr-sm" color="primary" size=".85rem" />
                      <span class="selection-chip_item--text text-primary">{{ value.name }}</span>
                    </q-chip>
                  </div>
                  <q-chip
                    v-else
                    class="glossy selection-chip_item active"
                    square
                    color="green-1"
                    clickable
                    size=".55rem"
                    icon-selected=""
                    @click="selectAttributeItem(index, value.code)"
                  >
                    <q-icon name="fa-solid fa-check" class="q-mr-sm" color="green-9" size=".85rem" />
                    <span class="selection-chip_item--text text-green-9">{{ value.name }}</span>
                  </q-chip>
                </template>
              </div>
            </div>
          </div>
          <div v-if="onSale && !outOfStock">
            <div class="flex items-end q-mb-lg">
              <div class="q-mr-lg">
                <span class="product-detail--quantity-title">Miktar</span>
                <input-piece v-model="quantity" />
              </div>
              <div>
                <q-btn size=".9rem" color="primary" @click="addToBasket" :loading="addToBasketLoading">
                  <span class="flex items-center text-weight-bold">
                    <q-icon name="shopping_basket" size="1rem" class="q-mr-sm" />
                    <span class="product-detail--actions_basket-btn">Sepete Ekle</span>
                  </span>
                </q-btn>
              </div>
            </div>
            <div>
              <q-btn
                v-if="!favouriteState.status"
                size="1em"
                outline
                color="red-7"
                unelevated
                class="text-capitalize bg-red-1 q-mr-md"
                no-caps
                @click="addToFavourites(product.code)"
                :loading="favouriteLoading || favouriteState.loading"
              >
                <q-icon name="fa-regular fa-heart" size="1rem" class="q-mr-sm" />
                <span class="product-detail--actions_fav-btn">Favorilerime Ekle</span>
              </q-btn>
              <q-btn
                v-else
                size="1em"
                color="red-7"
                unelevated
                class="text-capitalize bg-red-1 q-mr-md"
                no-caps
                @click="removeFromFavourites(product.code)"
                :loading="favouriteLoading || favouriteState.loading"
              >
                <q-icon name="fa-regular fa-heart" size="1rem" class="q-mr-sm" />
                <span class="product-detail--actions_fav-btn">Favorilerimden Çıkar</span>
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xs-12 q-py-md">
        <q-tabs
          v-model="tabsData.tab"
          inline-label
          outside-arrows
          mobile-arrows
          align="left"
          active-bg-color="primary"
          active-color="white"
          class="bg-white text-primary"
        >
          <q-tab name="aciklama" icon="description" label="Açıklama" />
          <q-tab name="ozellikler" icon="subject" label="Özellikler" />
          <q-tab name="yorumlar" icon="comment" label="Ürün Yorumları" />
        </q-tabs>
        <q-tab-panels v-model="tabsData.tab" animated transition-prev="jump-up" transition-next="jump-down">
          <q-tab-panel name="aciklama">
            <div class="product-detail--product-description" v-html="product.aciklama"></div>
          </q-tab-panel>

          <q-tab-panel name="ozellikler">
            <div class="product-tabs--features">
              <!--              <div class="urun-ozellik inline-block q-pa-sm" :key="key" v-for="(ozellik, key) in product.ozellikler">-->
              <!--                <p class="text-subtitle1 text-weight-regular">-->
              <!--                  <span class="text-grey-9">{{ key }}:</span>-->
              <!--                  <span>{{ ozellik }}</span>-->
              <!--                </p>-->
              <!--              </div>-->
              <q-list v-if="product.ozellikler.length" bordered>
                <q-item :key="key" v-for="(feature, key) in product.ozellikler">
                  <q-item-section class="text-weight-bold">
                    <q-item-label>{{ feature.name }}:</q-item-label>
                  </q-item-section>
                  <q-item-section>
                    <div class="flex">
                      <span>{{ feature.value }}</span>
                      <q-icon v-if="feature.desc" class="product-detail--features_info-icon q-ml-sm" color="primary" name="help_center" size="1rem">
                        <q-tooltip anchor="top right" self="top left" class="bg-secondary" style="font-size: 0.75rem">
                          <q-icon name="fa-solid fa-info" size=".75rem" class="q-mr-xs" style="position: relative; top: -2px" />
                          {{ feature.desc }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </q-tab-panel>

          <q-tab-panel name="yorumlar">
            <p class="text-body1 bg-grey-3 q-pa-md">
              <q-icon name="comment" left />
              Henüz yorum yapılmamış (0)
            </p>
          </q-tab-panel>
        </q-tab-panels>
      </div>
    </div>
  </div>
</template>

<style scoped>
#productDetail {
  font-family: 'EB Garamond', serif;
}

#productDetail h1 {
  letter-spacing: 0.05rem;
}

.product-detail--head_title {
  margin-bottom: 1.15rem;
}

.product-detail--head_product-code {
  font-size: 1.2rem;
  margin-bottom: 1.35rem;
  color: #555;
  letter-spacing: 0.045rem;
  display: flex;
  align-items: center;
}

.product-detail--not-on-sale {
  background: #fff;
  display: inline-block;
  width: auto;
  margin: 1rem 0 2rem;
  border: 3px solid #ff830040;
  border-radius: 8px;
}

.product-detail--out-of-stock {
  background: #fff;
  display: inline-block;
  width: auto;
  margin: 1rem 0 2rem;
  border: 3px solid rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.product-detail--not-on-sale p {
  font-size: 1rem;
  padding: 1rem 1.7rem;
  margin: 0;
  color: #4a2600;
  font-weight: 600;
  letter-spacing: 0.035rem;
  font-family: system-ui, -apple-system, Helvetica, sans-serif;
}

.product-detail--out-of-stock p {
  display: flex;
  align-items: center;
  font-size: 1rem;
  padding: 1rem 2.5rem 1rem 1.35rem;
  margin: 0;
  color: #030201;
  font-weight: 600;
  letter-spacing: 0.035rem;
  font-family: system-ui, -apple-system, Helvetica, sans-serif;
}

.product-detail--out-of-stock p svg {
  margin-right: 1rem;
}

.product-detail--actions_fav-btn {
  letter-spacing: 0.045rem;
}

.product-detail--actions_basket-btn {
  letter-spacing: 0.045rem;
}
</style>

<style>
.swiper {
  width: 100%;
  height: 100%;
}

.hzl-swiper-main {
  margin-bottom: 0.75rem;
  box-shadow: rgba(0, 0, 0, 0.1) -4px 9px 25px -6px;
  border: 1px solid #ededed;
}

.product-detail--slider .slider--thumbs {
  display: flex;
  align-items: center;
  position: relative;
}

.hz-swiper-main-thumbs {
  background: #dedede;
  border: 5px solid #dedede;
}

.hz-swiper-main-thumbs--nav {
  position: absolute;
}

.hz-swiper-main-thumbs--nav.thumbs--left {
  left: -40px;
}

.hz-swiper-main-thumbs--nav.thumbs--right {
  right: -40px;
}

.hz-swiper-main-thumbs--nav.thumbs--left .thumbs--s-btn {
  background: #dedede;
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
}

.hz-swiper-main-thumbs--nav.thumbs--right .thumbs--s-btn {
  background: #dedede;
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
}

.swiper-slide {
  text-align: center;
  font-size: 18px;
  background: #fff;

  /* Center slide text vertically */
  display: -webkit-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  -webkit-align-items: center;
  align-items: center;

  box-sizing: border-box;
  background: transparent;
}

.swiper-slide-thumb {
  background: var(--q-primary);
  border-radius: 3px;
  transform: scale(1);
  perspective: 1px;
}

.swiper-slide-thumb.swiper-slide-thumb-active img {
  border-radius: 2px !important;
  transition: 0.1s;
}

.swiper-slide-thumb.swiper-slide-thumb-active img:target {
  opacity: 0.7;
}

.swiper-slide-thumb.swiper-slide-thumb-active {
  border: 3px solid var(--q-primary);
  position: relative;
}

.swiper-slide-thumb.swiper-slide-thumb-active:after {
  content: '';
  position: absolute;
  display: inline-block;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 7px 10.2px 7px;
  border-color: transparent transparent var(--q-primary) transparent;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.swiper-slide img {
  cursor: pointer;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page_content {
  position: relative;
  width: 500px;
  height: 300px;

  .page1,
  .page2 {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

nav {
  display: flex;
  justify-content: center;
  align-items: center;
}

nav > a {
  padding: 20px 20px;
}

.pointer-none {
  pointer-events: none;
}
</style>

<style>
.product-detail--pricing,
.product-detail--selection-title {
  font-family: system-ui, -apple-system, sans-serif;
  letter-spacing: 0.055rem;
  font-size: 1.3rem;
}

.product-detail--pricing {
  font-weight: 600;
  font-size: 1.65rem;
  letter-spacing: 0.075rem;
}

.product-tabs--features {
  max-width: 500px;
}

.product-detail--features_info-icon {
  cursor: pointer;
}

.product-detail--selection-features {
  margin-bottom: 2rem;
  min-height: 20px;
}

.product-detail--selection-title {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  letter-spacing: 0.05rem;
  font-weight: 600;
  color: #1a1b1e;
}

.selection-features--chips {
  margin-bottom: 0.35rem;
}

.selection-features--chips .selection-chip_item {
  border: 1px solid #dfdfdf;
  margin: 0 0.5rem 1rem 0;
  box-shadow: none !important;
  font-weight: 500;
  padding: 1.1rem 0.9rem;
  border-radius: 0.65rem;
}

.selection-features--chips .selection-chip_item.active {
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0) 50%, rgba(0, 215, 44, 0.12) 51%, rgba(255, 255, 255, 0.04)) !important;
  border-color: rgba(143, 188, 143, 0.2);
}

.selection-features--chips .selection-chip_item .selection-chip_item--text {
  font-size: 0.85rem;
  letter-spacing: 0.05rem;
  font-weight: 600;
}

.product-detail--quantity-title {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  display: block;
  color: #1a1b1e;
}
</style>

<style>
@font-face {
  font-family: swiper-icons;
  src: url('data:application/font-woff;charset=utf-8;base64, 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');
  font-weight: 400;
  font-style: normal;
}

:root {
  --swiper-theme-color: #007aff;
}

:host {
  position: relative;
  display: block;
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
}

.swiper {
  margin-left: auto;
  margin-right: auto;
  position: relative;
  overflow: hidden;
  list-style: none;
  padding: 0;
  z-index: 1;
  display: block;
}

.swiper-vertical > .swiper-wrapper {
  flex-direction: column;
}

.swiper-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  transition-property: transform;
  transition-timing-function: var(--swiper-wrapper-transition-timing-function, initial);
  box-sizing: content-box;
}

.swiper-android .swiper-slide,
.swiper-ios .swiper-slide,
.swiper-wrapper {
  transform: translate3d(0px, 0, 0);
}

.swiper-horizontal {
  touch-action: pan-y;
}

.swiper-vertical {
  touch-action: pan-x;
}

.swiper-slide {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  position: relative;
  transition-property: transform;
  display: block;
}

.swiper-slide-invisible-blank {
  visibility: hidden;
}

.swiper-autoheight,
.swiper-autoheight .swiper-slide {
  height: auto;
}

.swiper-autoheight .swiper-wrapper {
  align-items: flex-start;
  transition-property: transform, height;
}

.swiper-backface-hidden .swiper-slide {
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.swiper-3d.swiper-css-mode .swiper-wrapper {
  perspective: 1200px;
}

.swiper-3d .swiper-wrapper {
  transform-style: preserve-3d;
}

.swiper-3d {
  perspective: 1200px;
}

.swiper-3d .swiper-cube-shadow,
.swiper-3d .swiper-slide {
  transform-style: preserve-3d;
}

.swiper-css-mode > .swiper-wrapper {
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.swiper-css-mode > .swiper-wrapper::-webkit-scrollbar {
  display: none;
}

.swiper-css-mode > .swiper-wrapper > .swiper-slide {
  scroll-snap-align: start start;
}

.swiper-css-mode.swiper-horizontal > .swiper-wrapper {
  scroll-snap-type: x mandatory;
}

.swiper-css-mode.swiper-vertical > .swiper-wrapper {
  scroll-snap-type: y mandatory;
}

.swiper-css-mode.swiper-free-mode > .swiper-wrapper {
  scroll-snap-type: none;
}

.swiper-css-mode.swiper-free-mode > .swiper-wrapper > .swiper-slide {
  scroll-snap-align: none;
}

.swiper-css-mode.swiper-centered > .swiper-wrapper::before {
  content: '';
  flex-shrink: 0;
  order: 9999;
}

.swiper-css-mode.swiper-centered > .swiper-wrapper > .swiper-slide {
  scroll-snap-align: center center;
  scroll-snap-stop: always;
}

.swiper-css-mode.swiper-centered.swiper-horizontal > .swiper-wrapper > .swiper-slide:first-child {
  margin-inline-start: var(--swiper-centered-offset-before);
}

.swiper-css-mode.swiper-centered.swiper-horizontal > .swiper-wrapper::before {
  height: 100%;
  min-height: 1px;
  width: var(--swiper-centered-offset-after);
}

.swiper-css-mode.swiper-centered.swiper-vertical > .swiper-wrapper > .swiper-slide:first-child {
  margin-block-start: var(--swiper-centered-offset-before);
}

.swiper-css-mode.swiper-centered.swiper-vertical > .swiper-wrapper::before {
  width: 100%;
  min-width: 1px;
  height: var(--swiper-centered-offset-after);
}

.swiper-3d .swiper-slide-shadow,
.swiper-3d .swiper-slide-shadow-bottom,
.swiper-3d .swiper-slide-shadow-left,
.swiper-3d .swiper-slide-shadow-right,
.swiper-3d .swiper-slide-shadow-top {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.swiper-3d .swiper-slide-shadow {
  background: rgba(0, 0, 0, 0.15);
}

.swiper-3d .swiper-slide-shadow-left {
  background-image: linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}

.swiper-3d .swiper-slide-shadow-right {
  background-image: linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}

.swiper-3d .swiper-slide-shadow-top {
  background-image: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}

.swiper-3d .swiper-slide-shadow-bottom {
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}

.swiper-lazy-preloader {
  width: 42px;
  height: 42px;
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -21px;
  margin-top: -21px;
  z-index: 10;
  transform-origin: 50%;
  box-sizing: border-box;
  border: 4px solid var(--swiper-preloader-color, var(--swiper-theme-color));
  border-radius: 50%;
  border-top-color: transparent;
}

.swiper-watch-progress .swiper-slide-visible .swiper-lazy-preloader,
.swiper:not(.swiper-watch-progress) .swiper-lazy-preloader {
  animation: swiper-preloader-spin 1s infinite linear;
}

.swiper-lazy-preloader-white {
  --swiper-preloader-color: #fff;
}

.swiper-lazy-preloader-black {
  --swiper-preloader-color: #000;
}

@keyframes swiper-preloader-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

#product-detail--head .discount {
  margin-right: 1rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #fff;
  padding: 3px 6px;
  background-color: #ef5350;
}

.on-sale-price.on-discount-price:after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  left: 0;
  transform: rotate(-8deg);
  top: 50%;
  background-color: #333;
}

.on-sale-price.on-discount-price {
  font-weight: 400;
  position: relative;
  color: #444444;
  font-size: 1.5rem;
  margin-left: 0.5rem;
}
</style>
